import React from "react";
import { FiTrendingUp, FiTrendingDown } from "react-icons/fi";

/**
 * Dashboard KPI card: icon, label, value, secondary info and optional trend.
 * Tones only tint the icon chip so the grid stays calm and readable.
 */
const TONES = {
    primary: "bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400",
    secondary: "bg-secondary-50 text-secondary-600 dark:bg-secondary-500/10 dark:text-secondary-400",
    success: "bg-success-50 text-success-600 dark:bg-success-500/10 dark:text-success-400",
    warning: "bg-warning-50 text-warning-600 dark:bg-warning-500/10 dark:text-warning-400",
    danger: "bg-danger-50 text-danger-600 dark:bg-danger-500/10 dark:text-danger-400",
    slate: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
};

export default function StatCard({
    label,
    value,
    icon: Icon,
    tone = "primary",
    trend,
    trendValue,
    hint,
    loading = false,
    className = "",
}) {
    if (loading) {
        return (
            <div className={`rounded-2xl border border-slate-200/80 bg-white p-4 dark:border-slate-800 dark:bg-slate-900 ${className}`}>
                <div className="animate-pulse">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <div className="h-3.5 w-20 rounded bg-slate-200 dark:bg-slate-800" />
                            <div className="mt-3 h-7 w-28 rounded bg-slate-200 dark:bg-slate-800" />
                        </div>
                        <div className="h-10 w-10 rounded-xl bg-slate-200 dark:bg-slate-800" />
                    </div>
                    <div className="mt-4 h-3.5 w-24 rounded bg-slate-200 dark:bg-slate-800" />
                </div>
            </div>
        );
    }

    return (
        <div
            className={`group rounded-2xl border border-slate-200/80 bg-white p-4 shadow-card transition-all duration-200
                hover:-translate-y-0.5 hover:shadow-card-hover
                dark:border-slate-800 dark:bg-slate-900 ${className}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="truncate text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {label}
                    </p>
                    <p className="mt-1.5 text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                        {value}
                    </p>
                </div>
                {Icon && (
                    <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${TONES[tone] || TONES.primary}`}>
                        <Icon className="h-5 w-5" />
                    </div>
                )}
            </div>

            {(trendValue || hint) && (
                <div className="mt-3 flex items-center gap-1.5 text-xs">
                    {trendValue ? (
                        <>
                            {trend === "down" ? (
                                <FiTrendingDown className="h-3.5 w-3.5 text-danger-500" />
                            ) : (
                                <FiTrendingUp className="h-3.5 w-3.5 text-success-500" />
                            )}
                            <span className={`font-semibold ${trend === "down" ? "text-danger-600 dark:text-danger-400" : "text-success-600 dark:text-success-400"}`}>
                                {trendValue}
                            </span>
                            {hint && <span className="truncate text-slate-500 dark:text-slate-400">{hint}</span>}
                        </>
                    ) : (
                        <span className="truncate text-slate-500 dark:text-slate-400">{hint}</span>
                    )}
                </div>
            )}
        </div>
    );
}
